import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Description, SmallHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { BlueButton, NavButton } from 'components/Button'
import { SearchForm, SelectBox } from 'components/Form'
import { CautionCard, MenuCard, ScheduleCardList } from 'components/Card'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getBreads } from 'reducks/bread/selectors'
import { getSchedules } from 'reducks/schedule/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { BreadType } from 'types/bread'
import { push } from 'connected-react-router'
import { filterBreadsByCategory, filterBreadsByKeyword, filterBreadsBySchedule } from 'lib/filter'
import { fetchBreads } from 'reducks/bread/operations'
import { fetchSchedules } from 'reducks/schedule/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { startOfMonth } from 'date-fns'
import { useCategoryOptions, useFetchCategories } from 'hooks/category'

const ScheduleTemplate: FC = () => {
  const dispatch = useDispatch()
  const type = 'preorder'
  const breads = useSelector((state: State) => getBreads(state))
  const schedules = useSelector((state: State) => getSchedules(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const categories = useFetchCategories()
  const sortKey = 'order'

  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [breadList, setBreadList] = useState<BreadType[]>([])

  const goToEditCollection = useCallback(() => {
    dispatch(push('/schedule/collective_setting'))
  }, [])

  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useCallback(
    (value: string) => {
      if (value === '' && keyword === '') {
        // カテゴリーが未選択かつキーワードも未選択であれば初期値に戻す
        const filteredBreadsBySchedule = filterBreadsBySchedule(breads, schedules)
        setBreadList([...filteredBreadsBySchedule])
      } else if (value === '' && keyword !== '') {
        // カテゴリーが未選択だがキーワードが入力されている場合
        const filteredBreadsBySchedule = filterBreadsBySchedule(breads, schedules)
        const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, filteredBreadsBySchedule)
        setBreadList([...filteredBreadsByKeyword])
      } else {
        // カテゴリでフィルターした後にキーワードでフィルタリング
        const filteredBreadsBySchedule = filterBreadsBySchedule(breads, schedules)
        const filteredBreadsByCategory = filterBreadsByCategory(value, categories, filteredBreadsBySchedule)
        const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, filteredBreadsByCategory)
        setBreadList([...filteredBreadsByKeyword])
      }
      setCategoryName(value)
    },
    [breads, categories, keyword, setCategoryName, setBreadList, schedules]
  )

  // 検索キーワードの入力
  const handleKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // 空白であればカテゴリーのみのフィルタリング
      if (value === '') {
        const filteredBreadsBySchedule = filterBreadsBySchedule(breads, schedules)
        const filteredBreadsByCategory = filterBreadsByCategory(categoryName, categories, filteredBreadsBySchedule)
        setBreadList([...filteredBreadsByCategory])
      }
      setKeyword(value)
    },
    [categoryName, categories, breads, setKeyword, setBreadList, schedules]
  )

  // キーワード検索(部分一致)
  const searchBreads = useCallback(
    (keyword: string) => {
      // カテゴリでフィルターした後にキーワードでフィルタリング
      const filteredBreadsBySchedule = filterBreadsBySchedule(breads, schedules)
      const filteredBreadsByCategory = filterBreadsByCategory(categoryName, categories, filteredBreadsBySchedule)
      const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, filteredBreadsByCategory)
      setBreadList([...filteredBreadsByKeyword])
    },
    [breads, categories, keyword, setCategoryName, setBreadList, schedules]
  )

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public', sortKey, type))
    dispatch(fetchSchedules(storeRef, [type]))
  }, [storeRef, sortKey, type])

  // 臨時休業日・営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // ReduxのStoreにあるbreadsをローカルのbreadListに反映する
  useEffect(() => {
    let _breads: BreadType[] = filterBreadsBySchedule(breads, schedules)
    // カテゴリが指定されていればフィルタリング
    if (categoryName !== '') {
      _breads = filterBreadsByCategory(categoryName, categories, _breads)
    }
    // 検索キーワードが入力されていればフィルタリング
    if (keyword !== '') {
      _breads = filterBreadsByKeyword(keyword, _breads)
    }
    setBreadList([..._breads])
  }, [breads, schedules])

  // カテゴリの選択肢を生成
  const categoryOptions = useCategoryOptions(categories)

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <Description
        description={'お客さまから事前予約商品の注文を受け付ける日を設定したり、1日あたりの販売上限数を変更できます。'}
      />
      <Spacer size={20} />
      <section>
        <Spacer size={20} />
        <SearchForm
          isFilled={keyword !== ''}
          onChange={handleKeyword}
          onClick={() => searchBreads(keyword)}
          placeholder={'商品名で検索する'}
          value={keyword}
        />
        <Spacer size={12} />
        <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryName} />
        <Spacer size={28} />
        <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
        <Spacer size={12} />
        {breads.length === 0 ? (
          // 事前予約設定済みの商品が存在しない場合
          <>
            <CautionCard
              align={'center'}
              caution={'設定できる商品がありません'}
              description={
                '事前予約日設定ができる商品が登録されておりません。sacriで出品する商品の管理から、事前予約を受け付ける商品を登録してください。'
              }
            />
            <Spacer size={20} />
            <MenuCard isPrimary={true} label={'sacriで出品する商品の管理'} url={'/product'} />
          </>
        ) : // キーワードやカテゴリでのフィルタリング結果が0件の場合
        breadList.length === 0 ? (
          <CautionCard
            align={'center'}
            caution={'該当商品がありません'}
            description={'別の方法で検索してみてください。'}
          />
        ) : (
          // 事前予約設定済みの商品が存在する場合
          <>
            <ScheduleCardList breads={breadList} type={type} />
            <Spacer size={48} />
            <BlueButton
              label={'商品の受渡日を一括設定する'}
              onClick={goToEditCollection}
              size={'medium'}
              type={'main'}
            />
          </>
        )}
      </section>
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default ScheduleTemplate
